import * as yup from "yup";
import { numeralMasked } from "@/composables/maskingUtils";
import { MinimumDrawAmount } from "@/models/loans";

export const ISO_8601_FORMAT = /^\d{4}-\d{2}-\d{2}$/;

export function isDateInPast(value?: null | string | Date) {
  if (value === undefined || value === null) {
    return true;
  }

  const today = new Date().setHours(0, 0, 0, 0);
  const input = new Date(value).setHours(0, 0, 0, 0);

  return input >= today;
}

export function getRequestedAmountValidation(
    maxRequestedAmount: number,
    getAvailableAmount: () => number | null,
		minimumDrawAmount?: MinimumDrawAmount | null
): yup.StringSchema {
    let originalNumericValue: number | null = null;

    return yup
        .string()
        .typeError('Requested Amount is invalid')
        .required('Requested Amount is required')
        .test('is-valid-number', 'Requested Amount must be a valid number', function (value) {
            if (!value) {
                return false;
            }

            const sanitizedValue = value.replace(/[$,]/g, '');

            return /^[0-9]+$/.test(sanitizedValue);
        })
        .test('is-above-min-limit', `Must be greater than ${minimumDrawAmount?.display || '$250'}`, function (value) {
            const sanitizedValue = value.replace(/[^0-9]/g, '');
            const numericValue = parseFloat(sanitizedValue);

            return numericValue >= (minimumDrawAmount?.amount || 250);
        })
        .test(
            'is-under-max-limit',
            function () {
                let maxLimit = getAvailableAmount() ?? maxRequestedAmount;

                if (originalNumericValue !== null && originalNumericValue > maxLimit) {
                    maxLimit = originalNumericValue;
                }

                return `Requested Amount cannot exceed ${numeralMasked(String(maxLimit))}`;
            },
            function (value) {
                if (!value) return false;

                const sanitizedValue = value.replace(/[^0-9]/g, '');
                const numericValue = parseFloat(sanitizedValue);

                const maxLimit = getAvailableAmount() ?? maxRequestedAmount;

                if (originalNumericValue === null) {
                    originalNumericValue = numericValue;
                }

                // allow if less than or equal to the original value or within the max limit
                return numericValue <= originalNumericValue || numericValue <= maxLimit;
            }
        );
}

export function getCertifiedAmountValidation(
	getRequestedAmount: () => null | undefined| number | null,
	getRequestedAmountFormatted: () => string | null) : yup.NumberSchema 
{ 
	const requestedAmount = getRequestedAmount();
	const requestedAmountFormatted = getRequestedAmountFormatted();
		return yup
			.number()
			.when([], {
				is: () => {
					const hasRequestedAmount = requestedAmount !== null && requestedAmount !== undefined && requestedAmount > 0;
					if (hasRequestedAmount) {
						return true;
					}
					
					return false;
				},
				then: (schema) =>
					schema
						.nullable()
						.transform((curr, orig) => (orig === "" ? null : curr))
						.test(
							"is-valid-test",
							`Certified Amount cannot exceed ${requestedAmountFormatted}`,
							(value, context) => {
								if (value === undefined || value === null) {
									return true;
								}
								
								const requestedAmount  = context.parent.requestedAmount;
								const requestedAmountCleaned = typeof requestedAmount === 'string' ? requestedAmount?.replace(/[^0-9.,]/g, '') : requestedAmount;
								const requestedAmountNumericValue = typeof requestedAmountCleaned === 'string' ? parseFloat(requestedAmountCleaned) : requestedAmountCleaned;
								
								if (requestedAmountNumericValue !== null && requestedAmountNumericValue !== undefined && value > requestedAmountNumericValue) {
									return false;
								} else {
									return true;
									}
								}),
					otherwise: (schema) => schema.nullable().notRequired(),
				});
	}